import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { UserPlus, Mail, Shield, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

export default function SettingsTab({ project, refreshProject }) {
  const [memberEmail, setMemberEmail] = useState('');
  const [permission, setPermission] = useState('view');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');
  const { getAuthHeaders, API, user } = useAuth();

  const isOwner = project.owner_id === user?.id;

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError('');
    setAdding(true);

    try {
      await axios.post(
        `${API}/projects/${project.id}/members`,
        { email: memberEmail, permission },
        getAuthHeaders()
      );
      setMemberEmail('');
      setPermission('view');
      refreshProject();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-heading font-bold text-slate-900 mb-2">Project Settings</h2>
        <p className="text-sm text-slate-600">Manage project members and permissions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Project Info */}
        <div className="card p-6">
          <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">Project Info</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Name</label>
              <p className="text-sm text-slate-900 font-medium mt-1">{project.name}</p>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Description</label>
              <p className="text-sm text-slate-700 mt-1">{project.description || 'No description'}</p>
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-slate-500">Created</label>
              <p className="text-sm text-slate-900 mt-1">
                {new Date(project.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Add Member */}
        {isOwner && (
          <div className="card p-6">
            <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <UserPlus size={20} />
              Add Member
            </h3>
            <form onSubmit={handleAddMember} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                <Input
                  type="email"
                  value={memberEmail}
                  onChange={(e) => setMemberEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  required
                  data-testid="member-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Permission</label>
                <Select value={permission} onValueChange={setPermission}>
                  <SelectTrigger data-testid="member-permission-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">View Only</SelectItem>
                    <SelectItem value="edit">Edit</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-md text-sm">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                disabled={adding || project.members.length >= 10}
                className="btn-primary w-full"
                data-testid="add-member-button"
              >
                {adding ? 'Adding...' : 'Add Member'}
              </Button>
              {project.members.length >= 10 && (
                <p className="text-xs text-orange-600 text-center">Maximum 10 members reached</p>
              )}
            </form>
          </div>
        )}

        {/* Members List */}
        <div className="card p-6">
          <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">
            Members ({project.members.length + 1}/10)
          </h3>
          <div className="space-y-3">
            {/* Owner */}
            <div className="bg-blue-50 border border-blue-200 rounded-md px-4 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                    <span className="text-xs font-bold text-white">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                </div>
                <span className="text-xs font-mono bg-blue-600 text-white px-2 py-1 rounded">OWNER</span>
              </div>
            </div>

            {/* Members */}
            {project.members.map((member, index) => (
              <div key={index} className="bg-slate-50 rounded-md px-4 py-3" data-testid="member-item">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield size={16} className="text-slate-400" />
                    <div>
                      <p className="text-sm font-medium text-slate-900">Member {index + 1}</p>
                      <p className="text-xs text-slate-500">{member.permission}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
